import { translate } from "@classdojo/web/utils/translate";
import combineActionHandlers from "@web-monorepo/infra/combineActionHandlers";
import createAction from "@web-monorepo/infra/createAction";
import createReducer from "@web-monorepo/infra/createReducer";
import { PodInstallFunction } from "@web-monorepo/shared/podInfra";
import sample from "lodash/sample";
import pick from "lodash/pick";
import map from "lodash/map";
import icon from "./assets/logo.svg";
import { Student } from "src/pods/students";
import { AppMetadata } from "src/pods/toolkitData/components/EditableSetup";
import computeOptimalBaseAvatarURL from "src/utils/computeOptimalBaseAvatarURL";

type RandomPayload = {
  student: Student;
  students: Student[];
  baseAvatarURL?: string;
};

const STATE_KEY = "random";

type RandomState = {
  student?: Student;
  students?: Student[];
  previouslyChosenStudents: Student[];
};

export type RandomSlice = {
  [STATE_KEY]: RandomState;
};

export const appMetadataRandom = (): AppMetadata => ({
  href: "random",
  title: translate("dojo.toolkit:apps.random", "Random"),
  backgroundColor: "dt_content_success",
  icon,
});

const initialState: RandomState = {
  student: undefined,
  students: undefined,
  previouslyChosenStudents: [],
};

export const selectPreviouslyChosenStudents = (state: RandomSlice) => state?.[STATE_KEY]?.previouslyChosenStudents;
export const selectCurrentStudent = (state: RandomSlice) => state?.[STATE_KEY]?.student;

export const RECEIVE_RANDOM = createAction("RECEIVE_RANDOM");

export function chooseRandomStudent(students?: Student[], previouslyChosenStudents?: Student[]) {
  if (!students?.length) throw new Error("`pickRandomStudent` requires students");

  const makeName = ({ firstName, lastName }: Student) => `${firstName} ${lastName}`;
  const prevStudentNames = previouslyChosenStudents?.map(makeName);
  const otherStudents = students?.filter((student: Student) => !prevStudentNames?.includes(makeName(student)));
  const chosenStudent = otherStudents.length ? sample(otherStudents) : sample(students);

  return chosenStudent;
}

export function displayStudent(students?: Student[], chosenStudent?: Student) {
  return {
    type: RECEIVE_RANDOM,
    payload: deflatePayload(students, chosenStudent),
  };
}

const pickRandomStudentHandler = createReducer(RECEIVE_RANDOM, (payload: RandomPayload, state: RandomState) => {
  const { students, student } = inflatePayload(payload);
  let previouslyChosenStudents: Student[];
  if (state.previouslyChosenStudents.length === students.length) {
    previouslyChosenStudents = [];
  } else {
    previouslyChosenStudents = state.previouslyChosenStudents.concat(student);
  }

  return { student, previouslyChosenStudents, students };
});

const reducer = combineActionHandlers(initialState, [pickRandomStudentHandler]);

/**
 * This function aims to return only the necessary student data to be sent over the network. We want
 * avoid sending unnecessary data in order to minimize traffic costs.
 *
 * @param  {Object}   chosenStudent
 * @param  {[]Object} students
 * @return {Object}
 */
function deflatePayload(students?: Student[], chosenStudent?: Student) {
  const onlyNecessaryStudentProps: (student: Student) => Partial<Student> = (s) =>
    pick(s, ["firstName", "lastName", "avatar"]);
  const baseAvatarURL = computeOptimalBaseAvatarURL(map(students, "avatar"));

  const optimizeAvatarURL = ({ avatar, ...student }: Partial<Student>) => ({
    ...student,
    avatar: avatar?.replace(baseAvatarURL!, ""),
  });

  return {
    student: optimizeAvatarURL(onlyNecessaryStudentProps(chosenStudent!)),
    students: students?.map(onlyNecessaryStudentProps).map(optimizeAvatarURL),
    baseAvatarURL,
  };
}

function inflatePayload(payload: RandomPayload) {
  let { student, students } = payload;
  const { baseAvatarURL } = payload;
  const inflateStudent = ({ firstName, lastName, avatar, ...rest }: Student) => {
    return {
      ...rest,
      firstName,
      lastName,
      avatar: `${baseAvatarURL || ""}${avatar}`,
    };
  };

  students = students.map(inflateStudent);
  student = inflateStudent(student);
  return { student, students };
}

const install: PodInstallFunction = (installReducer) => {
  installReducer(STATE_KEY, reducer);
};

export default install;
